import { z } from 'zod';
import { botScopeSchema, uuidV7Schema } from './ids';
import { sourceFactSchema, timestampSchema } from './assistant';
import { moneyMinorStringSchema } from './money';

export const businessCapabilitySchema = z.enum([
  'search_offerings',
  'create_lead',
  'record_appointment',
  'change_appointment',
  'adviser_directory',
  'appointment_host',
  'subscription_billing',
  'read_receivables',
  'payment_claim',
  'dispute',
  'payment_proposal',
  'collection_reminders',
]);
export const crmAdviserSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    email: z.email().nullable(),
  })
  .strict();
export const crmAdviserDirectorySchema = z
  .object({
    version: z.literal('assist-advisers-v1'),
    scope: botScopeSchema,
    crmCompanyId: z.string().uuid(),
    advisers: z.array(crmAdviserSchema).max(50),
    nextCursor: z.string().uuid().nullable(),
  })
  .strict();
export const adviserCrmLinkInputSchema = z
  .object({
    botId: uuidV7Schema,
    businessConnectionId: uuidV7Schema,
    crmCompanyId: z.string().uuid(),
    employeeId: z.string().uuid(),
  })
  .strict();
export const adviserCrmLinkSchema = adviserCrmLinkInputSchema
  .extend({ adviserId: uuidV7Schema })
  .strict();
export const businessManifestSchema = z
  .object({
    version: z.literal('assist-business-v1'),
    scope: botScopeSchema,
    businessName: z.string().min(1).max(200),
    catalogKind: z.enum(['property', 'general']).nullable(),
    capabilities: z.array(businessCapabilitySchema).min(1),
  })
  .strict();
export const verifiedSubjectSchema = z
  .object({
    scope: botScopeSchema,
    conversationId: uuidV7Schema,
    subject: z.string().min(1).max(200),
    verifiedUntil: timestampSchema,
  })
  .strict();
export const receivableSchema = z
  .object({
    reference: z.string().min(1).max(200),
    currency: z.enum(['PEN', 'USD']),
    outstandingMinor: moneyMinorStringSchema,
    dueDate: z.iso.date(),
    status: z.enum(['open', 'paid', 'disputed', 'cancelled']),
    version: z.string().min(1).max(200),
  })
  .strict();
export const accountSnapshotSchema = z
  .object({
    scope: botScopeSchema,
    subject: z.string().min(1),
    observedAt: timestampSchema,
    version: z.string().min(1),
    receivables: z.array(receivableSchema).max(25),
    nextCursor: z.string().min(1).max(200).nullable(),
    facts: z.array(sourceFactSchema).max(30),
  })
  .strict();
export type AccountSnapshot = z.infer<typeof accountSnapshotSchema>;

export const caseResolutionSchema = z
  .object({
    scope: botScopeSchema,
    caseId: uuidV7Schema,
    subject: z.string().min(1),
    reference: z.string().min(1),
    kind: z.enum(['payment_claim', 'dispute']),
    outcome: z.enum([
      'claim_rejected',
      'partial_payment_confirmed',
      'payment_confirmed',
      'dispute_resolved',
    ]),
    ledgerVersion: z.string().min(1),
    reviewedAt: timestampSchema,
  })
  .strict();
export type CaseResolution = z.infer<typeof caseResolutionSchema>;

export const businessAcknowledgementSchema = z
  .object({
    scope: botScopeSchema,
    idempotencyKey: uuidV7Schema,
    reference: z.string().min(1),
    status: z.enum(['accepted', 'confirmed', 'pending']),
    facts: z.array(sourceFactSchema).max(30),
  })
  .strict();
export const collectionReminderSchema = z
  .object({
    id: uuidV7Schema,
    subject: z.string().min(1),
    receivableReference: z.string().min(1),
    ledgerVersion: z.string().min(1),
    recipient: z.email(),
    connectionId: uuidV7Schema,
    dueAt: timestampSchema,
    authorization: z
      .object({
        basis: z.string().min(1).max(2000),
        policyVersion: z.string().min(1),
        expiresAt: timestampSchema,
        timezone: z.string().min(1),
        workingDays: z.array(z.number().int().min(0).max(6)).min(1),
        opensAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u),
        closesAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u),
        minimumIntervalHours: z.number().int().min(1).max(720),
        maximumAttempts: z.number().int().min(1).max(30),
      })
      .strict()
      .superRefine((policy, context) => {
        try {
          new Intl.DateTimeFormat('en', { timeZone: policy.timezone });
        } catch {
          context.addIssue({
            code: 'custom',
            message: 'Invalid contact timezone.',
          });
        }
        if (policy.opensAt >= policy.closesAt)
          context.addIssue({
            code: 'custom',
            message: 'Contact closing time must follow opening time.',
          });
      }),
  })
  .strict();
export type CollectionReminder = z.infer<typeof collectionReminderSchema>;
